import { Box } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { horizontalTabsMenuStyleProps } from "../theme/main";

export type HorizontalTabsMenuItem = {
  id: string;
  title: string;
};

type ActiveUnderlineStyle = {
  left: number;
  width: number;
};

type HorizontalTabsMenuProps = {
  items: HorizontalTabsMenuItem[];
  activeItemId: string;
  ariaLabel?: string;
  onChange?: (item: HorizontalTabsMenuItem) => void;
};

export function HorizontalTabsMenu({
  items,
  activeItemId,
  ariaLabel = "Section menu",
  onChange,
}: HorizontalTabsMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [activeUnderlineStyle, setActiveUnderlineStyle] =
    useState<ActiveUnderlineStyle>({ left: 0, width: 0 });
  const {
    itemGroupStyle,
    menuUnderlineStyle,
    itemStyle,
    activeItemStyle,
    activeUnderlineStyle: activeUnderlineThemeStyle,
  } = horizontalTabsMenuStyleProps;
  const activeUnderlineHeight = activeUnderlineThemeStyle.height ?? "2px";

  useLayoutEffect(() => {
    const updateActiveUnderline = () => {
      const menu = menuRef.current;
      const activeItem = itemRefs.current[activeItemId];

      if (!menu || !activeItem) {
        setActiveUnderlineStyle({ left: 0, width: 0 });
        return;
      }

      setActiveUnderlineStyle({
        left: activeItem.offsetLeft,
        width: activeItem.offsetWidth,
      });
    };

    updateActiveUnderline();
    window.addEventListener("resize", updateActiveUnderline);
    return () => window.removeEventListener("resize", updateActiveUnderline);
  }, [activeItemId, items]);

  return (
    <Box
      ref={menuRef}
      role="tablist"
      aria-label={ariaLabel}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        ...itemGroupStyle,
        ...menuUnderlineStyle,
      }}
    >
      {items.map((item) => {
        const isActive = item.id === activeItemId;

        return (
          <Box
            key={item.id}
            ref={(element: HTMLButtonElement | null) => {
              itemRefs.current[item.id] = element;
            }}
            component="button"
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange?.(item)}
            sx={{
              appearance: "none",
              border: 0,
              background: "transparent",
              margin: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              lineHeight: 1,
              textAlign: "left",
              whiteSpace: "nowrap",
              ...itemStyle,
              ...(isActive ? activeItemStyle : {}),
            }}
          >
            {item.title}
          </Box>
        );
      })}
      <Box
        sx={{
          position: "absolute",
          bottom: `calc(${activeUnderlineHeight} * -1)`,
          left: 0,
          width: activeUnderlineStyle.width,
          transform: `translateX(${activeUnderlineStyle.left}px)`,
          transition: "transform 220ms ease, width 220ms ease",
          pointerEvents: "none",
          ...activeUnderlineThemeStyle,
        }}
      />
    </Box>
  );
}
